import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

type AppRole = "student" | "growlearn" | "aca_member" | "admin";

const ROLE_PRIORITY: Record<AppRole, number> = {
  admin: 4,
  aca_member: 3,
  growlearn: 2,
  student: 1,
};

interface AuthContextType {
  session: Session | null;
  user: User | null;
  role: AppRole | null;
  fullName: string;
  loading: boolean;
  signOut: () => Promise<void>;
  updateFullName: (name: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  role: null,
  fullName: "",
  loading: true,
  signOut: async () => {},
  updateFullName: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(true);
  const fetchedForUser = useRef<string | null>(null);

  const loadUserData = async (userId: string) => {
    if (fetchedForUser.current === userId) return;
    fetchedForUser.current = userId;

    const [rolesRes, profileRes] = await Promise.all([
      supabase.from("user_roles").select("role").eq("user_id", userId),
      supabase.from("profiles").select("full_name").eq("user_id", userId).maybeSingle(),
    ]);

    if (rolesRes.data && rolesRes.data.length > 0) {
      const highest = rolesRes.data.reduce((best, curr) => {
        const currRole = curr.role as AppRole;
        return ROLE_PRIORITY[currRole] > ROLE_PRIORITY[best] ? currRole : best;
      }, rolesRes.data[0].role as AppRole);
      setRole(highest);
    }

    if (profileRes.data?.full_name) {
      setFullName(profileRes.data.full_name);
    }
  };

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) loadUserData(session.user.id);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserData(session.user.id);
      } else {
        setRole(null);
        setFullName("");
        fetchedForUser.current = null;
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setRole(null);
    setFullName("");
    fetchedForUser.current = null;
  };

  const updateFullName = async (name: string) => {
    if (!user) throw new Error("Not signed in");
    const { error } = await supabase.from("profiles").update({ full_name: name }).eq("user_id", user.id);
    if (error) throw error;
    setFullName(name);
  };

  return (
    <AuthContext.Provider value={{ session, user, role, fullName, loading, signOut, updateFullName }}>
      {children}
    </AuthContext.Provider>
  );
};
